import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, fromEvent, combineLatest } from 'rxjs';
import { map, startWith, shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SidebarStateService {
  private readonly expandedSubject = new BehaviorSubject<boolean>(true);

  /**
   * Observable that emits whether we're in mobile view
   */
  private readonly isMobileView$: Observable<boolean> = fromEvent(
    window,
    'resize'
  ).pipe(
    startWith(null),
    map(() => window.innerWidth < 1024), // lg breakpoint in Tailwind
    shareReplay(1)
  );

  /**
   * Observable that emits the current sidebar expansion state
   */
  readonly isExpanded$: Observable<boolean> =
    this.expandedSubject.asObservable();

  /**
   * Get the current expansion state
   */
  get isExpanded(): boolean {
    return this.expandedSubject.value;
  }

  /**
   * Check if currently in mobile view
   */
  get isMobileView(): boolean {
    return window.innerWidth < 1024;
  }

  /**
   * Set the sidebar expansion state
   */
  setExpanded(expanded: boolean): void {
    this.expandedSubject.next(expanded);
  }

  /**
   * Get the current sidebar width in pixels
   * Returns 0 for mobile view, actual width for desktop
   */
  get sidebarWidth(): number {
    if (this.isMobileView) {
      return 0; // Mobile sidebar is fixed positioned and doesn't affect content width
    }
    return this.isExpanded ? 240 : 56;
  }

  /**
   * Observable that emits the current sidebar width
   * Considers both expansion state and mobile/desktop view
   */
  get sidebarWidth$(): Observable<number> {
    return combineLatest([this.isMobileView$, this.expandedSubject]).pipe(
      map(([isMobile, expanded]) => {
        if (isMobile) {
          return 0; // Mobile sidebar doesn't affect content width
        }
        return expanded ? 240 : 56;
      })
    );
  }
}
